import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ion } from "@/components/Ion";
import { api } from "@/lib/api";
import { Section, inputCls } from "@/components/quote/QuoteParts";

const VEHICLES = [["car", "Car / coupe"], ["truck", "Pickup truck"], ["suv", "SUV / wagon"]];

export const VehicleShortcut = ({ onFill }) => (
  <Section title="QUICK START · WHOLE VEHICLE" testId="vehicle-shortcut">
    <div className="flex flex-wrap gap-2">
      {VEHICLES.map(([k, label]) => (
        <button key={k} onClick={() => onFill(k)} data-testid={`vehicle-fill-${k}`} className="rounded-full border border-[#4CCC06] px-4 py-2 text-xs font-extrabold text-[#4CCC06] transition-colors hover:bg-[#4CCC0619]">{label}</button>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-[#A0A0A0]">Fills in doors, hood, trunk, roof, fenders, quarter panels, bumpers and frame with typical sizes. Change anything below, then add suspension parts.</p>
  </Section>
);

export const SaveBar = ({ loaded, onSave, testId = "history-save" }) => {
  const [name, setName] = useState(loaded?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(loaded?.name || ""); }, [loaded]);
  const save = async (asNew) => {
    if (!name.trim()) return toast.error("Give this quote a name first, e.g. 'Smith kitchen'");
    setBusy(true);
    try { await onSave(name.trim(), asNew); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[14px] border border-[#333] bg-[#1A1A1A] p-3" data-testid={testId}>
      <input className={`${inputCls} min-w-[180px] flex-1`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this quote, e.g. Smith kitchen" data-testid={`${testId}-name`} />
      <button disabled={busy} onClick={() => save(!loaded)} data-testid={`${testId}-btn`} className="rounded-[10px] bg-[#4CCC06] px-4 py-2.5 text-xs font-extrabold tracking-[1px] text-black disabled:opacity-60">{loaded ? "SAVE CHANGES" : "SAVE TO HISTORY"}</button>
      {loaded && <button disabled={busy} onClick={() => save(true)} data-testid={`${testId}-new`} className="rounded-[10px] border border-[#4CCC06] px-4 py-2.5 text-xs font-extrabold tracking-[1px] text-[#4CCC06] disabled:opacity-60">SAVE AS NEW</button>}
    </div>
  );
};

const fmt = (s) => new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export const HistoryPanel = ({ onOpen, reloadKey }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  useEffect(() => { if (open) api("/quote-builder/history").then(setList).catch((e) => toast.error(e.message)); }, [open, reloadKey]);
  const del = async (q) => {
    if (!window.confirm(`Delete "${q.name}" from history?`)) return;
    try { await api(`/quote-builder/history/${q.id}`, { method: "DELETE" }); setList((xs) => xs.filter((x) => x.id !== q.id)); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mb-3" data-testid="history-panel">
      <button onClick={() => setOpen((o) => !o)} data-testid="history-toggle" className="flex w-full items-center justify-between rounded-[12px] border border-[#333] bg-[#1A1A1A] px-4 py-3 text-left text-[13px] font-extrabold transition-colors hover:border-[#555]">
        <span className="flex items-center gap-2"><Ion name="time-outline" size={16} color="#4CCC06" />Quote History</span><Ion name={open ? "chevron-up" : "chevron-down"} size={14} color="#A0A0A0" />
      </button>
      {open && (
        <div className="mt-2 rounded-[12px] border border-[#333] bg-[#121212] p-2" data-testid="history-list">
          {!list ? <p className="p-3 text-xs text-[#A0A0A0]">Loading…</p> : !list.length ? <p className="p-3 text-xs text-[#A0A0A0]" data-testid="history-empty">No saved quotes yet. Build one and tap Save to History.</p> : list.map((q) => (
            <div key={q.id} className="flex items-center gap-3 border-b border-[#222] p-2.5 last:border-0" data-testid={`history-item-${q.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-bold">{q.name}</p>
                <p className="truncate text-[11px] text-[#A0A0A0]">{fmt(q.updated_at)} · {q.created_by_name}{q.summary?.builders ? ` · ${q.summary.builders}` : ""}{q.summary?.price ? ` · ${q.summary.price}` : ""}</p>
              </div>
              <button onClick={async () => { try { onOpen(await api(`/quote-builder/history/${q.id}`)); setOpen(false); } catch (e) { toast.error(e.message); } }} data-testid={`history-open-${q.id}`} className="rounded-full border border-[#4CCC06] px-3 py-1.5 text-[11px] font-extrabold text-[#4CCC06]">OPEN</button>
              <button onClick={() => del(q)} data-testid={`history-delete-${q.id}`} className="p-1.5" aria-label="Delete"><Ion name="trash-outline" size={15} color="#EF4444" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const money = (n) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const MarketRange = ({ lines, labels, idx }) => {
  const [bm, setBm] = useState(null);
  useEffect(() => {
    api(`/quote-builder/benchmarks?types=${[...new Set(lines.map((l) => l.art))].join(",")}`).then(setBm).catch(() => setBm({ types: {} }));
  }, [lines]);
  if (!bm) return null;
  const rows = lines.filter((l) => bm.types[l.art] && l.sq_in > 0);
  return (
    <div className="-mt-3 mb-8 rounded-[14px] border border-dashed border-[#333] bg-[#121212] p-4" data-testid={`market-range-${idx}`}>
      <p className="mb-1 text-[10px] font-extrabold tracking-[2px] text-[#4CCC06]">WHAT OTHER ALR FRANCHISEES WIN JOBS AT (INTERNAL)</p>
      {!rows.length ? <p className="text-[11px] leading-4 text-[#A0A0A0]" data-testid={`market-range-empty-${idx}`}>Not enough won jobs yet for these items. Ranges appear once at least {bm.min_jobs} invoiced jobs from {bm.min_locations}+ other locations exist.</p> : rows.map((l, i) => {
        const b = bm.types[l.art], mine = (l.quantity * l.unit_price) / (l.sq_in / 144);
        return (
          <div key={i} className="mt-2 flex flex-wrap items-baseline justify-between gap-2 text-xs" data-testid={`market-row-${idx}-${l.art}`}>
            <span className="font-bold">{labels[l.art]}</span>
            <span className="text-[#A0A0A0]">others {money(b.per_sqft.low)}–{money(b.per_sqft.high)}/sq ft{b.per_hour ? ` · ${money(b.per_hour.low)}–${money(b.per_hour.high)}/laser hr` : ""} · you {money(Math.round(mine * 100) / 100)}/sq ft <span className="text-[#666]">({b.jobs} jobs)</span></span>
          </div>
        );
      })}
    </div>
  );
};
